/* eslint-disable security/detect-object-injection */
import {media} from '@nfq/react-grid';

import {isDefined, isDefinedAndPopulated} from 'Application/utils/typeGuards';
import {breakpoints} from 'UI/utils/globalStyles';

import type {Media} from 'types/payload-types';

interface VideoSource {
    media?: string;
    src: string;
    type: string;
}

/**
 * A custom hook that processes the video data and returns a list of video sources based on breakpoints.
 * It checks the provided video data and iterates over different breakpoints, adding video sources with the appropriate `media`, `src`, and `type` properties.
 * The hook ensures that the sources are correctly populated for different screen sizes.
 *
 * @param video The video data or URL to be processed, which can be an object or a string representing the video source.
 * @returns An object containing the `sources` array with video source objects, each including media query, source URL, and MIME type.
 *
 * @example
 * ```tsx
 * const { sources } = usePayloadVideo(videoData);
 * ```
 */
export const usePayloadVideo = (video: Media | string | null | undefined) => {
    const sources: VideoSource[] = [];
    const sortedBreakpoints = breakpoints.toReversed() as unknown as ('lg' | 'md' | 'sm' | 'xl' | 'xs')[];

    if (isDefinedAndPopulated(video)) {
        (sortedBreakpoints as unknown as ('lg' | 'md' | 'sm' | 'xl' | 'xs')[]).forEach(breakpoint => {
            if (isDefined(video.sizes?.[breakpoint]?.url) && isDefined(video.sizes[breakpoint].mimeType)) {
                sources.push({
                    media: media(breakpoint).replace('@media only screen and ', ''),
                    src: video.sizes[breakpoint].url,
                    type: video.sizes[breakpoint].mimeType
                });
            }
        });

        if (isDefined(video.url) && isDefined(video.mimeType)) {
            sources.push({
                media: media('xs').replace('@media only screen and ', ''),
                src: video.url,
                type: video.mimeType
            });
        }
    }

    return {sources};
};